import { useEffect,useMemo,useRef,useState } from 'react'
import { getLibrary,moveDocument,setDocumentSessionDate,type Library,type Notebook } from './library'
import type { CalendarStudyEvent } from './calendarEventNotes'
import DawwinIcon from './DawwinIcon'

type Props={documentId?:string;onOpenStudySpace?:(id:string,name:string)=>void}

export default function NoteStudySpacePicker({documentId,onOpenStudySpace}:Props){
 const[data,setData]=useState<Library|null>(null)
 const[open,setOpen]=useState(false)
 const[query,setQuery]=useState('')
 const[busy,setBusy]=useState(false)
 const[error,setError]=useState('')
 const[dateBusy,setDateBusy]=useState(false)
 const[sessionOpen,setSessionOpen]=useState(false)
 const[sessionBusy,setSessionBusy]=useState(false)
 const[sessionQuery,setSessionQuery]=useState('')
 const[sessionEvents,setSessionEvents]=useState<CalendarStudyEvent[]>([])
 const[sessionLoading,setSessionLoading]=useState(false)
 const[calendarContext,setCalendarContext]=useState<{linked:boolean;calendarId?:string;eventId?:string;event?:{summary?:string;start?:{dateTime?:string;date?:string};htmlLink?:string}|null}>({linked:false})
 const root=useRef<HTMLDivElement|null>(null)

 const load=async()=>{try{setData(await getLibrary())}catch(e){console.error(e)}}
 const loadCalendarContext=async()=>{if(!documentId)return;try{const r=await fetch(`https://api.dawwin.al-ilm.net/calendar/event-notes/${encodeURIComponent(documentId)}`,{credentials:'include'});if(r.ok)setCalendarContext(await r.json())}catch(e){console.error(e)}}
 useEffect(()=>{if(documentId){void load();void loadCalendarContext()}},[documentId])
 useEffect(()=>{
  const refresh=()=>void load()
  window.addEventListener('dawwin:library-change',refresh)
  return()=>window.removeEventListener('dawwin:library-change',refresh)
 },[])
 useEffect(()=>{
  if(!open&&!sessionOpen)return
  const onPointer=(event:MouseEvent)=>{if(root.current&&!root.current.contains(event.target as Node)){setOpen(false);setSessionOpen(false)}}
  const onKey=(event:KeyboardEvent)=>{if(event.key==='Escape'){setOpen(false);setSessionOpen(false)}}
  document.addEventListener('mousedown',onPointer)
  document.addEventListener('keydown',onKey)
  return()=>{document.removeEventListener('mousedown',onPointer);document.removeEventListener('keydown',onKey)}
 },[open,sessionOpen])

 const nodes=data?.notebooks||[]
 const assignedId=documentId?(data?.documents||[]).find(d=>d.qirtaas_document_id===documentId)?.notebook_id||null:null
 const nodeMap=useMemo(()=>new Map(nodes.map(n=>[n.id,n])),[nodes])
 const path=(id:string|null)=>{
  if(!id)return ''
  const parts:string[]=[]
  let current=nodeMap.get(id)
  const seen=new Set<string>()
  while(current&&!seen.has(current.id)){
   seen.add(current.id);parts.unshift(current.name)
   current=current.parent_id?nodeMap.get(current.parent_id):undefined
  }
  return parts.join(' › ')
 }
 const assignedPath=path(assignedId)
 const sessionDate=documentId?(data?.documents||[]).find(d=>d.qirtaas_document_id===documentId)?.session_date||'':''
 const options=useMemo(()=>{
  const q=query.trim().toLowerCase()
  return nodes.map(node=>({node,path:pathFor(node,nodes)}))
   .filter(x=>!q||x.path.toLowerCase().includes(q))
   .sort((a,b)=>a.path.localeCompare(b.path))
 },[nodes,query])

 async function changeSessionDate(value:string){
  if(!documentId||dateBusy)return
  setDateBusy(true);setError('')
  try{await setDocumentSessionDate(documentId,value||null);await load();window.dispatchEvent(new Event('dawwin:library-change'))}
  catch(e){console.error(e);setError('Could not update the session date.')}
  finally{setDateBusy(false)}
 }

 async function assign(next:string|null){
  if(!documentId||busy||next===assignedId)return
  setBusy(true);setError('')
  try{
   await moveDocument(documentId,next)
   await load()
   window.dispatchEvent(new Event('dawwin:library-change'))
   setOpen(false);setQuery('')
  }catch(e){console.error(e);setError('Could not move this note.')}
  finally{setBusy(false)}
 }


 async function openSessionPicker(){
  if(sessionOpen){setSessionOpen(false);return}
  setSessionOpen(true);setOpen(false);setSessionLoading(true);setError('')
  try{
   const status=await fetch('https://api.dawwin.al-ilm.net/calendar/status',{credentials:'include'}).then(r=>r.json())
   if(!status.connected){setError('Connect Google Calendar first.');return}
   const calData=await fetch('https://api.dawwin.al-ilm.net/calendar/calendars',{credentials:'include'}).then(r=>r.json())
   const calendars:{id:string;summary:string}[]=calData.items||[]
   const centre=new Date((sessionDate||new Date().toISOString().slice(0,10))+'T12:00:00')
   const timeMin=new Date(centre.getTime()-370*86400000).toISOString(),timeMax=new Date(centre.getTime()+60*86400000).toISOString()
   const batches=await Promise.all(calendars.map(async cal=>{const q=new URLSearchParams({calendarId:cal.id,timeMin,timeMax});const r=await fetch('https://api.dawwin.al-ilm.net/calendar/events?'+q,{credentials:'include'});if(!r.ok)return[];const d=await r.json();return(d.items||[]).map((e:CalendarStudyEvent)=>({...e,calendarId:cal.id,calendarName:cal.summary}))}))
   const centreMs=centre.getTime();setSessionEvents((batches.flat() as CalendarStudyEvent[]).filter(e=>!e.dawwinDocumentId).sort((a,b)=>Math.abs(calendarEventTime(a)-centreMs)-Math.abs(calendarEventTime(b)-centreMs)))
  }catch(e){console.error(e);setError('Could not load calendar sessions.')}finally{setSessionLoading(false)}
 }
 async function linkSession(event:CalendarStudyEvent){
  if(!documentId||sessionBusy)return;setSessionBusy(true);setError('')
  try{
   const r=await fetch('https://api.dawwin.al-ilm.net/calendar/event-notes',{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({calendarId:event.calendarId||'',eventId:event.id,documentId})})
   if(!r.ok)throw new Error('link_failed')
   const date=(event.start?.dateTime||event.start?.date||'').slice(0,10);if(date)await setDocumentSessionDate(documentId,date)
   await Promise.all([load(),loadCalendarContext()]);setSessionOpen(false);setSessionQuery('');window.dispatchEvent(new Event('dawwin:library-change'));window.dispatchEvent(new Event('dawwin:calendar-change'))
  }catch(e){console.error(e);setError('Could not link this note to that session.')}finally{setSessionBusy(false)}
 }
 async function unlinkSession(){
  if(!documentId||sessionBusy)return;setSessionBusy(true);setError('')
  try{const r=await fetch(`https://api.dawwin.al-ilm.net/calendar/event-notes/${encodeURIComponent(documentId)}`,{method:'DELETE',credentials:'include'});if(!r.ok)throw new Error('unlink_failed');setCalendarContext({linked:false});window.dispatchEvent(new Event('dawwin:calendar-change'))}
  catch(e){console.error(e);setError('Could not unlink this calendar session.')}finally{setSessionBusy(false)}
 }
 const sessionTargetMs=new Date((sessionDate||new Date().toISOString().slice(0,10))+'T12:00:00').getTime()
 const sessionQ=sessionQuery.trim().toLowerCase()
 const sessionNearby=sessionEvents.filter(e=>Math.abs(calendarEventTime(e)-sessionTargetMs)<=7*86400000)
 const sessionFiltered=sessionQ?sessionEvents.filter(e=>((e.summary||'')+' '+(e.calendarName||'')).toLowerCase().includes(sessionQ)):(sessionNearby.length?sessionNearby:sessionEvents.slice(0,30))

 if(!documentId)return null
 return <div className="note-study-space" ref={root}>
  <label className="note-session-date"><span>Session date</span><input type="date" value={sessionDate} disabled={dateBusy} onChange={e=>void changeSessionDate(e.target.value)} title="Actual lecture / session date"/></label>
  <span className="note-study-space-label">Calendar session</span>
  <button className={`note-study-space-trigger ${calendarContext.linked?'assigned':'unfiled'}`} type="button" onClick={()=>void openSessionPicker()} aria-expanded={sessionOpen} title={calendarContext.linked?'View linked calendar session':'Link this note to a calendar session'}>
   <span>{sessionBusy?'Saving…':calendarContext.linked?(calendarContext.event?.summary||'Linked session'):'+ Link session'}</span><DawwinIcon className="note-study-space-chevron" name="chevronDown"/>
  </button>
  {sessionOpen&&<div className="note-study-space-popover note-calendar-popover">
   <div className="note-study-space-popover-head"><div><strong>{calendarContext.linked?'Linked calendar session':'Link to calendar session'}</strong><small>{calendarContext.linked?((calendarContext.event?.start?.dateTime||calendarContext.event?.start?.date||'').slice(0,10)||'Linked session'):'Choose the lecture/class this note belongs to'}</small></div><button type="button" onClick={()=>setSessionOpen(false)} aria-label="Close">×</button></div>
   {calendarContext.linked?<div className="note-calendar-current"><strong>{calendarContext.event?.summary||'Calendar session'}</strong>{calendarContext.event?.htmlLink&&<a href={calendarContext.event.htmlLink} target="_blank" rel="noreferrer">Open in Google Calendar ↗</a>}<button type="button" disabled={sessionBusy} onClick={()=>void unlinkSession()}>Unlink session</button></div>:<>
    <input autoFocus value={sessionQuery} onChange={e=>setSessionQuery(e.target.value)} placeholder="Search calendar sessions…" aria-label="Search calendar sessions"/>
    {sessionLoading?<p className="note-study-space-error">Loading calendar sessions…</p>:<div className="note-study-space-options">{sessionFiltered.slice(0,40).map((e,i)=><button type="button" key={(e.calendarId||'')+':'+e.id+':'+i} disabled={sessionBusy} onClick={()=>void linkSession(e)}><span>{e.summary||'Untitled event'}</span><small>{formatCalendarEvent(e)}{e.calendarName?' · '+e.calendarName:''}</small></button>)}{!sessionFiltered.length&&<p>No unlinked sessions found near this note date. Try searching by class title.</p>}</div>}</>}
   {error&&<p className="note-study-space-error">{error}</p>}
  </div>}
  <span className="note-study-space-label">Study space</span>
  <div className="note-study-space-assignment">
   <button className={`note-study-space-trigger ${assignedId?'assigned':'unfiled'}`} type="button" onClick={()=>setOpen(v=>!v)} aria-expanded={open} title={assignedPath||'Assign this note to a Study Space'}>
    <span>{busy?'Moving…':assignedPath||'+ Assign to Study Space'}</span><DawwinIcon className="note-study-space-chevron" name="chevronDown"/>
   </button>
   {assignedId&&onOpenStudySpace&&<button className="note-study-space-open" type="button" onClick={()=>onOpenStudySpace(assignedId,nodeMap.get(assignedId)?.name||'Study Space')} title="Open this Study Space">Open Study Space ↗</button>}
  </div>
  {open&&<div className="note-study-space-popover">
   <div className="note-study-space-popover-head">
    <div><strong>{assignedId?'Move note':'Assign note'}</strong><small>{assignedPath||'Currently unfiled'}</small></div>
    <button type="button" onClick={()=>setOpen(false)} aria-label="Close">×</button>
   </div>
   <input autoFocus value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search programme, semester or module…" aria-label="Search Study Spaces"/>
   <div className="note-study-space-options">
    {assignedId&&<button className="unfile" type="button" disabled={busy} onClick={()=>void assign(null)}><span>Unfile note</span><small>Keep it in the library without a Study Space</small></button>}
    {options.map(({node,path})=><button className={node.id===assignedId?'current':''} type="button" key={node.id} disabled={busy} onClick={()=>void assign(node.id)}>
      <span>{path}</span>{node.id===assignedId&&<small>Current location</small>}
    </button>)}
    {!options.length&&<p>No Study Spaces match “{query}”.</p>}
   </div>
   {error&&<p className="note-study-space-error">{error}</p>}
  </div>}
 </div>
}

function pathFor(node:Notebook,nodes:Notebook[]){
 const map=new Map(nodes.map(n=>[n.id,n]))
 const parts:string[]=[]
 let current:Notebook|undefined=node
 const seen=new Set<string>()
 while(current&&!seen.has(current.id)){
  seen.add(current.id);parts.unshift(current.name)
  current=current.parent_id?map.get(current.parent_id):undefined
 }
 return parts.join(' › ')
}

function calendarEventTime(event:CalendarStudyEvent){return new Date(event.start?.dateTime||((event.start?.date||'')+'T12:00:00')).getTime()}
function formatCalendarEvent(event:CalendarStudyEvent){const raw=event.start?.dateTime||event.start?.date||'';if(!raw)return 'Date unavailable';const d=new Date(event.start?.dateTime||raw+'T12:00:00');return d.toLocaleDateString([],{day:'numeric',month:'short',year:'numeric'})+(event.start?.dateTime?' · '+d.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}):'')}
